/**
 * Maps the nested camelCase draft to the flat snake_case submission body, and
 * reference rows back to UI shapes. Subscores are keyed by DB id, resolved here
 * from the stable category `code` via a supplied map.
 */

import { HIGH_SCHOOL_START_TERM } from '@backrs_ui/common-ui';

import { compareISO, toISODate } from '@backrs_ui/common-ui/src/utils/dateTime.util';

import { v4 as uuidv4 } from 'uuid';

import {
  DEFAULT_STUDY_DAYS,
  EMPTY_ACT_SCORES,
  EMPTY_SAT_SCORES,
  HOURS_PER_DAY,
  resolveDraftStartDate,
  testForCategoryCode,
  testsInSelection,
} from './satActBooster.constants';
import {
  Archetype,
  BoosterDraft,
  BoosterPhase,
  BoosterPlan,
  BoosterProjection,
  DailyQuestion,
  DailyQuestionOption,
  FocusPref,
  InsufficientSchedule,
  InsufficientScheduleReason,
  PlanDay,
  PlanDayKind,
  PlanEntry,
  PlanResource,
  PlanTarget,
  ProjectionCurvePoint,
  ProjectionSection,
  RawReportFacts,
  RawReportSchool,
  RawSchoolMidRange,
  ReportFacts,
  ReportSchool,
  ResourceTiming,
  SCHOOL_TIERS,
  SchoolMidRange,
  SchoolTier,
  ScoreCategoryRef,
  TargetTest,
  TestDate,
  WhichTests,
} from './satActBooster.types';

type SubscoreInput = Readonly<{
  score_category_id: number;
  percent_correct: number | null;
  value_low: number | null;
  value_high: number | null;
}>;

/** Flat submission body shared by compute (subset) and save (full). */
export type BoosterSubmissionInput = Readonly<{
  name: string | null;
  /** The student's own graduation year; back-filled onto the profile on save, or on the guest claim. */
  expected_graduation_year: number | null;
  /** Program start term/year; back-filled onto the profile like the graduation year. */
  program_start_term: string | null;
  program_start_year: number | null;
  /** The student's own school; back-filled onto the profile on save, or on the guest claim. */
  school: string | null;
  /** The student's own zip; back-filled onto the profile on save, or on the guest claim. */
  zip_code: string | null;
  /** Null for a "Both" taker before the report resolves the stronger test; the server derives it. */
  target_test: TargetTest | null;
  act_composite: number | null;
  act_english: number | null;
  act_math: number | null;
  act_reading: number | null;
  act_science: number | null;
  act_writing: number | null;
  sat_total: number | null;
  sat_erw: number | null;
  sat_math: number | null;
  target_score: number | null;
  test_date: string | null;
  /** The plan's calendar anchor; today unless the student picked another day. */
  start_date: string;
  hours_per_day: number;
  focus_pref: BoosterDraft['targets']['focusPref'];
  schools: { institution_id: number | null; name: string | null }[];
  study_days: number[];
  blackouts: { start_date: string; end_date: string; reason: string | null }[];
  subscores?: SubscoreInput[];
  report_content?: string | null;
}>;

/**
 * The test the report is built for. A single-test taker reports that test. A "Both" taker
 * doesn't pick — the server resolves the stronger test — so this is null until the report
 * comes back (at which point the resolved test is adopted into `targets.targetTest`).
 */
export const targetTestForDraft = (draft: BoosterDraft): TargetTest | null => {
  if (draft.whichTests === 'ACT') return 'act';
  if (draft.whichTests === 'SAT') return 'sat';

  return draft.targets.targetTest;
};

type FlattenOptions = Readonly<{
  /** When provided, includes the reporting-category breakdown keyed by resolved id. */
  categoryIdByCode?: Record<string, number>;
  /** When provided (including null), sets report_content on the body. */
  reportContent?: string | null;
}>;

export const flattenBoosterDraft = (
  draft: BoosterDraft,
  options: FlattenOptions = {},
): BoosterSubmissionInput => {
  const { actScores, satScores, targets } = draft;

  const map = options.categoryIdByCode;

  // Which test a submission is for is derived from these columns, so the selection is enforced
  // here: a deselected test is withheld on the way out rather than cleared in the draft.
  const keeps = testsInSelection(draft.whichTests);
  const act = keeps.act ? actScores : EMPTY_ACT_SCORES;
  const sat = keeps.sat ? satScores : EMPTY_SAT_SCORES;

  // The save replaces the whole set, so a deselected test's stored rows clear with them.
  const selected = draft.subscores.filter((subscore) => {
    const test = testForCategoryCode(subscore.categoryCode);

    return test === undefined || keeps[test];
  });
  const resolved = map
    ? selected.flatMap((subscore): SubscoreInput[] => {
        const id = map[subscore.categoryCode];

        // A code with no active id would fail save-time validation.
        return id === undefined
          ? []
          : [
              {
                score_category_id: id,
                percent_correct: subscore.percentCorrect,
                value_low: subscore.valueLow,
                value_high: subscore.valueHigh,
              },
            ];
      })
    : undefined;
  // A partial set would replace the stored breakdown, deleting the rows that didn't resolve.
  const subscores = resolved?.length === selected.length ? resolved : undefined;

  return {
    name: draft.firstName || null,
    expected_graduation_year: draft.expectedGraduationYear,
    program_start_term: HIGH_SCHOOL_START_TERM,
    program_start_year: draft.programStartYear,
    school: draft.school || null,
    zip_code: draft.zip || null,
    target_test: targetTestForDraft(draft),
    act_composite: act.composite,
    act_english: act.english,
    act_math: act.math,
    act_reading: act.reading,
    act_science: act.science,
    act_writing: act.writing,
    sat_total: sat.total,
    sat_erw: sat.erw,
    sat_math: sat.math,
    target_score: targets.targetScore,
    test_date: targets.testDate,
    start_date: resolveDraftStartDate(targets.startDate),
    hours_per_day: targets.hoursPerDay,
    focus_pref: targets.focusPref,
    schools: targets.schools.map((school) => ({
      institution_id: school.institutionId,
      name: school.name,
    })),
    study_days: targets.studyDays,
    blackouts: targets.blackouts.map((blackout) => ({
      start_date: blackout.startDate,
      end_date: blackout.endDate,
      reason: blackout.reason ?? null,
    })),
    ...(subscores && draft.ownsSubscores ? { subscores } : {}),
    ...(options.reportContent !== undefined ? { report_content: options.reportContent } : {}),
  };
};

/** Raw submission row (snake_case columns + relations) as returned by the most-recent endpoint. */
export type RawBoosterSubmission = Readonly<{
  id: string;
  name: string | null;
  act_composite: number | null;
  act_english: number | null;
  act_math: number | null;
  act_reading: number | null;
  act_science: number | null;
  act_writing: number | null;
  sat_total: number | null;
  sat_erw: number | null;
  sat_math: number | null;
  target_test: TargetTest | null;
  target_score: number | null;
  test_date: string | null;
  start_date: string | null;
  hours_per_day: number | null;
  focus_pref: FocusPref | null;
  report_content: string | null;
  completed_at: string | null;
  schools?: { institution_id: number | null; name: string | null }[];
  blackouts?: { start_date: string; end_date: string; reason: string | null }[];
  studyDays?: { weekday: number }[];
  /** Absent from responses that don't join the breakdown; the code is null if that join is skipped. */
  subscores?: {
    score_category_code: string | null;
    percent_correct: number | null;
    value_low: number | null;
    value_high: number | null;
  }[];
}>;

// Date columns can arrive as full datetime strings; keep the YYYY-MM-DD prefix only.
const dateOnly = (value: string): string => value.slice(0, 10);

/**
 * A stored anchor at or before today drops to null, which reads as today: a past day is rejected on
 * save, and a pinned today would go stale if the resumed session sits open past midnight.
 */
const restoredStartDate = (raw: string | null): string | null => {
  if (!raw) return null;

  const startDate = dateOnly(raw);

  return compareISO(startDate, toISODate(new Date())) <= 0 ? null : startDate;
};

const whichTestsFromScores = (raw: RawBoosterSubmission): WhichTests | null => {
  const hasAct = raw.act_composite !== null && raw.act_composite !== undefined;
  const hasSat = raw.sat_total !== null && raw.sat_total !== undefined;

  if (hasAct && hasSat) return 'Both';
  if (hasAct) return 'ACT';
  if (hasSat) return 'SAT';

  return null;
};

/**
 * The stored breakdown on draft shape, owned only when every row could be placed. A code the local
 * taxonomy doesn't carry has no home in the draft, and a partial set must not be saved back:
 * doing so replaces the stored breakdown with the rows that happened to map.
 */
const isPlaceableCode = (code: string | null): code is string =>
  code !== null && testForCategoryCode(code) !== undefined;

const restoreSubscores = (
  raw: RawBoosterSubmission,
): Pick<BoosterDraft, 'subscores' | 'ownsSubscores'> => {
  const rows = raw.subscores;

  if (rows === undefined) return { subscores: [], ownsSubscores: false };

  const subscores = rows.flatMap(({ score_category_code: code, ...values }) =>
    isPlaceableCode(code)
      ? [
          {
            categoryCode: code,
            percentCorrect: values.percent_correct,
            valueLow: values.value_low,
            valueHigh: values.value_high,
          },
        ]
      : [],
  );

  if (subscores.length !== rows.length) {
    console.warn('Discarding subscores outside the local taxonomy', {
      id: raw.id,
      dropped: rows
        .map(({ score_category_code: code }) => code)
        .filter((code) => !isPlaceableCode(code)),
    });
  }

  return { subscores, ownsSubscores: subscores.length === rows.length };
};

/**
 * Rebuild the wizard draft from a saved submission so a resumed run can be finished. Name is
 * restored; zip/school/graduation-year are not, since the intake step is behind a resumed run.
 */
export const restoreDraftFromSubmission = (raw: RawBoosterSubmission): BoosterDraft => ({
  firstName: raw.name ?? '',
  expectedGraduationYear: null,
  programStartYear: null,
  zip: '',
  school: '',
  whichTests: whichTestsFromScores(raw),
  actScores: {
    composite: raw.act_composite,
    english: raw.act_english,
    math: raw.act_math,
    reading: raw.act_reading,
    science: raw.act_science,
    writing: raw.act_writing,
  },
  satScores: { total: raw.sat_total, erw: raw.sat_erw, math: raw.sat_math },
  ...restoreSubscores(raw),
  targets: {
    targetTest: raw.target_test,
    targetScore: raw.target_score,
    testDate: raw.test_date ? dateOnly(raw.test_date) : null,
    startDate: restoredStartDate(raw.start_date),
    // hours_per_day is a DB decimal (can arrive as a string); coerce to the numeric draft field.
    hoursPerDay: raw.hours_per_day == null ? HOURS_PER_DAY.default : Number(raw.hours_per_day),
    focusPref: raw.focus_pref ?? 'single',
    // Study days are only written at finalize, so a run restored mid-flight has none. Fall back to
    // the same defaults a fresh run starts with rather than an empty week the student must refill.
    studyDays: raw.studyDays?.length
      ? raw.studyDays.map((day) => day.weekday)
      : [...DEFAULT_STUDY_DAYS],
    schools: (raw.schools ?? []).map((school) => ({
      institutionId: school.institution_id ?? null,
      name: school.name ?? '',
    })),
    blackouts: (raw.blackouts ?? []).map((blackout) => ({
      id: uuidv4(),
      startDate: blackout.start_date,
      endDate: blackout.end_date,
      reason: blackout.reason ?? undefined,
    })),
  },
});

/** Build the code → score_category_id map from the reference rows. */
export const buildCategoryIdByCode = (
  categories: readonly ScoreCategoryRef[],
): Record<string, number> =>
  Object.fromEntries(categories.map((category) => [category.code, category.id]));

type RawTestDateRow = Readonly<{ test: TargetTest; test_date: string }>;
type RawScoreCategoryRow = Readonly<{ id: number; code: string; test: TargetTest }>;

export const mapTestDates = (rows: readonly RawTestDateRow[]): TestDate[] =>
  rows.map((row) => ({ test: row.test, date: row.test_date }));

export const mapScoreCategories = (rows: readonly RawScoreCategoryRow[]): ScoreCategoryRef[] =>
  rows.map((row) => ({ id: row.id, code: row.code, test: row.test }));

/** One entry on a plan day. `section` drives the FE color; category/skill feed the day detail. */
type RawPlanEntry = Readonly<{
  section: string;
  categoryName: string | null;
  /** Stable reporting-category code; absent on entries the engine couldn't resolve to a category. */
  categoryCode?: string | null;
  skillTarget: string | null;
  /** The reporting category the day's quiz draws from; absent until the question pipeline joins it. */
  scoreCategoryId?: number | null;
  /** The engine-resolved resource link for the entry; absent or null when none was emitted. */
  resource?: {
    url: string;
    label: string;
    note?: string | null;
    timingMinutes?: number | null;
    questionCount?: number | null;
  } | null;
}>;

/** Study-plan block joined onto the most-recent response by the calendar engine. */
export type RawBoosterPlan = Readonly<{
  stats: { pointsToGain: number | null; totalWeeks: number; hoursPerWeek: number };
  /** Non-blocking schedule-adequacy warning; reasons are open strings so unknown codes don't break the map. */
  insufficientSchedule?: { flag: boolean; reasons: readonly string[] } | null;
  /** Composite-band study profile; an open string so an unknown future code passes through unbroken. */
  archetype?: string | null;
  /** Time-to-test phase; an open string so an unknown future code passes through unbroken. */
  phase?: string | null;
  /** Per-section study-time allocation; only section + share are consumed, other fields are ignored. */
  sections?: readonly { section: string; share: number }[];
  calendar: {
    startDate: string;
    testDate: string | null;
    days: readonly {
      date: string;
      kind: PlanDayKind;
      completed: boolean;
      /** The day's latest recorded quiz attempt; null when none was recorded. */
      attempt: { score: number; total: number } | null;
      weekNumber: number;
      entries: readonly RawPlanEntry[];
    }[];
  };
  /** Recompute-on-read score projection; null/absent when it can't be projected. */
  projection?: RawBoosterProjection | null;
}>;

type RawProjectionSection = Readonly<{
  section: string;
  current: number;
  projected: number;
  ci95: number;
}>;

/** Score-projection block; its field names already match the mapped shape. */
export type RawBoosterProjection = Readonly<{
  current: number | null;
  weeksRemaining: number;
  weeklyHours: number;
  composite: number;
  ci95: number;
  sections: readonly RawProjectionSection[];
  curve: readonly {
    extraHours: number;
    weeklyHours: number;
    composite: number;
    ci95: number;
    sections: readonly RawProjectionSection[];
  }[];
}>;

type RawBlackout = NonNullable<RawBoosterSubmission['blackouts']>[number];

/** The reason for an off day, matched from the submission's blackout ranges (ISO dates compare lexically). */
const blackoutReasonForDay = (
  day: RawBoosterPlan['calendar']['days'][number],
  blackouts: readonly RawBlackout[],
): string | undefined => {
  if (day.kind !== 'off') return undefined;

  const covering = blackouts.find(
    (b) => day.date >= dateOnly(b.start_date) && day.date <= dateOnly(b.end_date),
  );

  return covering?.reason ?? undefined;
};

/**
 * Carry the engine's schedule-adequacy warning through unchanged. Reason codes pass through as-is
 * (an unknown code renders a generic line downstream rather than being dropped); null when absent.
 */
const mapInsufficientSchedule = (
  raw: RawBoosterPlan['insufficientSchedule'],
): InsufficientSchedule | null =>
  raw ? { flag: raw.flag, reasons: raw.reasons as readonly InsufficientScheduleReason[] } : null;

// Falls back to the section name until a genuine, distinct reporting-category name arrives, so
// an entry reads as "no category yet" rather than carrying a plausible-but-invented one.
const resolveCategory = (section: string, categoryName: string | null): string => {
  if (categoryName && categoryName !== section) return categoryName;

  return section;
};

const isPositive = (value: number | null | undefined): value is number =>
  value != null && value > 0;

// Collapses rather than reaching the card as "0 minutes, 0 questions".
const mapTiming = (resource: NonNullable<RawPlanEntry['resource']>): ResourceTiming | null => {
  const { timingMinutes, questionCount } = resource;

  return isPositive(timingMinutes) && isPositive(questionCount)
    ? { minutes: timingMinutes, questionCount }
    : null;
};

const mapResource = (resource: RawPlanEntry['resource']): PlanResource | null =>
  resource
    ? {
        url: resource.url,
        label: resource.label,
        note: resource.note ?? null,
        timing: mapTiming(resource),
      }
    : null;

const mapPlanDay = (
  day: RawBoosterPlan['calendar']['days'][number],
  blackouts: readonly RawBlackout[],
): PlanDay => {
  const entries = day.entries ?? [];

  return {
    date: day.date,
    kind: day.kind,
    completed: day.completed,
    attempt: day.attempt ?? null,
    week: day.weekNumber,
    entries: entries.map(
      (entry): PlanEntry => ({
        section: entry.section,
        label: entry.categoryName ?? entry.section,
        resource: mapResource(entry.resource),
      }),
    ),
    // A null `skillTarget` still carries a category, a resource, and a quiz join.
    targets:
      day.kind === 'study'
        ? entries.map(
            (entry): PlanTarget => ({
              section: entry.section,
              category: resolveCategory(entry.section, entry.categoryName),
              categoryCode: entry.categoryCode ?? null,
              skillText: entry.skillTarget,
              scoreCategoryId: entry.scoreCategoryId ?? null,
              resource: mapResource(entry.resource),
            }),
          )
        : [],
    note: blackoutReasonForDay(day, blackouts),
  };
};

const mapProjectionSection = (section: RawProjectionSection): ProjectionSection => ({
  section: section.section,
  current: section.current,
  projected: section.projected,
  ci95: section.ci95,
});

/**
 * Carry the recompute-on-read score projection through unchanged. Null (card renders nothing) when
 * the block is absent or its what-if curve is empty.
 */
const mapProjection = (raw?: RawBoosterProjection | null): BoosterProjection | null => {
  if (!raw || !Array.isArray(raw.curve) || raw.curve.length === 0) return null;

  return {
    current: raw.current ?? null,
    weeksRemaining: raw.weeksRemaining,
    weeklyHours: raw.weeklyHours,
    composite: raw.composite,
    ci95: raw.ci95,
    sections: (raw.sections ?? []).map(mapProjectionSection),
    curve: raw.curve.map(
      (point): ProjectionCurvePoint => ({
        extraHours: point.extraHours,
        weeklyHours: point.weeklyHours,
        composite: point.composite,
        ci95: point.ci95,
        sections: (point.sections ?? []).map(mapProjectionSection),
      }),
    ),
  };
};

/**
 * Resolve the study plan for the output page from the joined `plan` block. Returns null when the
 * block or its calendar is absent, so the page renders its other sections without a calendar.
 */
export const mapPlanBlock = (
  submission: RawBoosterSubmission,
  raw?: RawBoosterPlan | null,
): BoosterPlan | null => {
  if (!raw?.calendar || !Array.isArray(raw.calendar.days) || !raw.stats) return null;

  const blackouts = submission.blackouts ?? [];

  return {
    startDate: raw.calendar.startDate,
    testDate: raw.calendar.testDate ?? null,
    stats: {
      pointsToGain: raw.stats.pointsToGain,
      totalWeeks: raw.stats.totalWeeks,
      hoursPerWeek: raw.stats.hoursPerWeek,
    },
    insufficientSchedule: mapInsufficientSchedule(raw.insufficientSchedule),
    // Carry the profile code through verbatim; an unknown code renders nothing rather than being dropped.
    archetype: (raw.archetype ?? null) as Archetype | null,
    // Carry the phase code through verbatim; an unknown code renders no chip rather than being dropped.
    phase: (raw.phase ?? null) as BoosterPhase | null,
    sections: (raw.sections ?? []).map((section) => ({
      section: section.section,
      share: section.share,
    })),
    days: raw.calendar.days.map((day) => mapPlanDay(day, blackouts)),
    projection: mapProjection(raw.projection),
  };
};

/** One option row on a bank question (`answer` copy, the correct flag, and its own rationale). */
type RawBoosterQuestionOption = Readonly<{
  id: string;
  sequence: number;
  answer: string;
  is_correct: boolean;
  explanation?: string | null;
  /** Present only when the option carries a diagram. */
  media_url?: string | null;
}>;

/** A bank question row with its ordered options, as the questions endpoint returns it. */
export type RawBoosterQuestion = Readonly<{
  id: string;
  passage: string | null;
  figures?: readonly { url?: string | null; label?: string | null }[];
  question_text: string;
  explanation: string | null;
  strand_id: number | null;
  /** Present only if the strand is graph-fetched; the by-skill breakdown labels groups with it. */
  strand?: { strand_name?: string | null } | null;
  options?: readonly RawBoosterQuestionOption[];
}>;

/** Map a bank question row (snake_case, ordered options) to the UI question shape. */
export const mapQuestion = (raw: RawBoosterQuestion): DailyQuestion => ({
  id: raw.id,
  passage: raw.passage ?? null,
  // Server order is the printed order; never re-sorted.
  figures: (raw.figures ?? [])
    .map((figure) => ({ url: figure.url?.trim() || '', label: figure.label?.trim() || null }))
    .filter((figure) => figure.url),
  stem: raw.question_text,
  explanation: raw.explanation ?? null,
  strandId: raw.strand_id ?? null,
  strandName: raw.strand?.strand_name ?? null,
  options: [...(raw.options ?? [])]
    .sort((a, b) => a.sequence - b.sequence)
    .map(
      (option): DailyQuestionOption => ({
        id: option.id,
        sequence: option.sequence,
        text: option.answer,
        isCorrect: option.is_correct,
        explanation: option.explanation?.trim() || null,
        mediaUrl: option.media_url?.trim() || null,
      }),
    ),
});

const isSchoolTier = (value: string): value is SchoolTier =>
  (SCHOOL_TIERS as readonly string[]).includes(value);

// Bounds that aren't both finite and ordered describe nothing. A median outside them only
// narrows to null — the bounds alone still place a score.
const mapMidRange = (raw: RawSchoolMidRange | null): SchoolMidRange | null => {
  if (raw === null) return null;

  const { low, median, high } = raw;

  if (
    low === null ||
    high === null ||
    !Number.isFinite(low) ||
    !Number.isFinite(high) ||
    low > high
  ) {
    console.warn('Discarding unusable mid-50% range', raw);

    return null;
  }

  const isUsableMedian =
    median !== null && Number.isFinite(median) && median >= low && median <= high;

  return { low, median: isUsableMedian ? median : null, high };
};

// Re-checked despite arriving normalized: this lands in an href, and a scheme-less string
// links relative to our own origin rather than failing visibly.
const mapSiteUrl = (raw: string | null): string | null => {
  if (raw === null) return null;

  const trimmed = raw.trim();

  if (!/^https?:\/\//i.test(trimmed)) {
    console.warn('Discarding unusable school site URL', raw);

    return null;
  }

  return trimmed;
};

// Without this the union is an assertion over wire data rather than a guarantee.
export const mapReportSchool = (raw: RawReportSchool): ReportSchool => ({
  ...raw,
  tier: raw.tier !== null && isSchoolTier(raw.tier) ? raw.tier : null,
  actMid50: mapMidRange(raw.actMid50),
  satMid50: mapMidRange(raw.satMid50),
  siteUrl: mapSiteUrl(raw.siteUrl),
});

export const mapReportFacts = (raw: RawReportFacts): ReportFacts => ({
  ...raw,
  schools: raw.schools.map(mapReportSchool),
});
